import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle, Shield } from '../../icons';
import {
  Avatar, Badge, Button, EmptyState, PageHeader, Table, Tabs,
  type TableColumn,
} from '../../components/shared';
// 쪽 머리 경로 · 범위 칩은 메뉴 배치에서(2026-10-17 · web 레인 W6 — lib/page-crumbs.ts)
import { crumbsFor, scopeFor } from '../../lib/page-crumbs';
import { fetchAnomalies, type AnomaliesDto, type AnomalyRow } from '../../api/hr-anomalies';
import {
  ANOMALY_LABEL, ANOMALY_TONE, filterAnomalies, orgText, type AnomalyFilter,
} from '../../lib/anomalies';
import './Anomalies.css';

/**
 * 이상 근태 — **숫자를 사람으로 펼친 자리** (2026-08-29 · 판 F).
 *
 * ★★ **왜 이 화면이 생겼나.** 근태 현황 다섯 칸이 「퇴근 누락 3 · 미출근 10」을 말하는데
 *    **누구인지 볼 데가 없었다.** 숫자만으로는 아무도 조치할 수 없다.
 *
 * ★★ **세는 규칙을 요약과 한 벌로 쓴다** — 퇴근 누락은 **어제 이전**, 미출근은 **오늘**.
 *    같은 화면의 위아래가 다른 수를 말하면 둘 다 못 믿게 된다. 그래서 그 사실을 **화면이 적는다**.
 * ★ **칩이 곧 필터다** — 「퇴근 누락 3」을 보고 눌렀는데 목록이 안 걸리면 다시 눈으로 찾게 된다.
 * ★ 볼 권한이 없으면 **그렇게 말한다**(403) — 빈 목록으로 얼버무리지 않는다.
 *
 * ── 정본 채택 (2026-09-04 · 채택 3단계 ③ 근태 · 판 G) ─────────────────────────
 * 손으로 짠 머리·칩·목록을 정본 컴포넌트로 옮겼다 — `PageHeader` · `Tabs(pill)` ·
 * `Table` · `Badge` · `EmptyState`. 정본 P3 「목록」이 요구하는 대로
 * **필터 칩에 건수**를 적고, 비어 있음을 **세 가지로 갈라** 말한다(없다 / 걸러서 없다 / 못 본다).
 *
 * ★★ **「퇴근 누락은 어제 이전 N일」은 화면에 남긴다** — 도움말로 보내지 않는다.
 *    이 문장은 「원래 그런 규칙」이 아니라 **지금 이 목록이 무엇을 센 것인가**이기 때문이다(정본 P8).
 * ★★ **조회 기록 안내도 화면에 남긴다** — 남의 근태를 여는 행위에 딸린 고지라,
 *    눌러야 보이는 자리에 두면 안 본 채로 조회하게 된다.
 * ★ 반대로 「마감은 사람이 한다」는 **원래 규칙**이라 도움말로 옮겼다.
 */
const ERRORS: Record<string, string> = {
  FORBIDDEN: '이상 근태는 회사 관리자 · 근태 담당자만 볼 수 있습니다.',
};

export function AnomaliesPage(): JSX.Element {
  const [data, setData] = useState<AnomaliesDto | null>(null);
  const [filter, setFilter] = useState<AnomalyFilter>('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForbidden(false);
    try {
      setData(await fetchAnomalies());
    } catch (err) {
      const code = (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
      // 옛 서버에는 이 라우트가 없다(404) — 그때도 "없다" 고 말한다(빈 화면으로 두지 않는다).
      setForbidden(code === 'FORBIDDEN');
      setError(code ? ERRORS[code] ?? '이상 근태를 불러오지 못했습니다.' : '이상 근태를 불러오지 못했습니다.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const rows = data ? filterAnomalies(data.rows, filter) : [];

  /** 칩의 숫자는 **전체 기준**이다 — 걸러도 안 바뀌어야 다시 돌아올 수 있다(정본 P3). */
  const tabs = useMemo(() => (data
    ? [
      { value: 'ALL', label: '전부', count: data.counts.total },
      { value: 'MISSING_CHECKOUT', label: ANOMALY_LABEL.MISSING_CHECKOUT, count: data.counts.missingCheckout },
      { value: 'ABSENT', label: ANOMALY_LABEL.ABSENT, count: data.counts.absent },
    ]
    : []), [data]);

  const columns: ReadonlyArray<TableColumn<AnomalyRow>> = useMemo(() => [
    {
      key: 'workDate',
      header: '날짜',
      width: '104px',
      render: (r) => <span className="anom__date">{r.workDate}</span>,
    },
    {
      key: 'who',
      header: '사람',
      width: '260px',
      render: (r) => (
        <span className="anom__who">
          <Avatar userId={Number(r.userId)} name={r.name} size="sm" />
          <span className="anom__whotext">
            <b className="anom__name">{r.name}</b>
            <span className="anom__org">{orgText(r)}{r.position ? ` · ${r.position}` : ''}</span>
          </span>
        </span>
      ),
    },
    {
      key: 'kind',
      header: '종류',
      width: '104px',
      // 색만으로 뜻을 만들지 않는다 — 배지 안에 «말» 이 함께 있다(정본 P5).
      render: (r) => (
        <Badge variant={ANOMALY_TONE[r.kind] === 'bad' ? 'danger' : 'warning'} size="sm">
          {ANOMALY_LABEL[r.kind]}
        </Badge>
      ),
    },
    {
      key: 'fact',
      header: '무엇이 어긋났나',
      render: (r) => (
        <span className="anom__fact">
          {r.kind === 'MISSING_CHECKOUT'
            ? <>출근 {r.checkIn} · 퇴근 기록 없음</>
            : <>기록 없음{r.baseStart ? ` · 기준 ${r.baseStart}` : ''}</>}
        </span>
      ),
    },
  ], []);

  return (
    <div className="anom">
      <PageHeader
        breadcrumb={crumbsFor('/erp/attendance/anomalies')}
        scope={scopeFor('/erp/attendance/anomalies')}
        title="이상 근태"
        description={data
          ? <>오늘 {data.date} 기준 · <b>퇴근 누락은 어제 이전 {data.days}일</b>에서 셉니다 — 오늘 퇴근을 안 찍은 사람은 대개 아직 일하는 중입니다.</>
          : '오늘 어긋난 것을 사람 단위로 봅니다'}
        help={{
          title: '이 화면의 규칙',
          body: (
            <>
              <p><b>마감은 사람이 합니다.</b> 서버가 몰래 닫으면 「그날 몇 시에 갔나」가 영영 사라집니다 —
                퇴근 누락은 <b>내 근태</b>의 「마감하지 않은 날」에서 본인이 마감합니다.</p>
              <p><b>미출근</b>은 오늘 기록이 없는 사람, <b>퇴근 누락</b>은 어제 이전에 출근만 찍힌 날입니다.
                주말·휴일은 세지 않습니다.</p>
              <p>화면에는 <b>시각 · 상태만</b> 나옵니다. 휴가 종류나 사유는 결재 문서 안에서만 볼 수 있습니다.</p>
            </>
          ),
        }}
      />

      {/* ★ 고지는 «눌러야 보이는 자리»에 두지 않는다 — 조회하기 전에 읽어야 하는 문장이다. */}
      <p className="anom__privacy">
        <AlertCircle size={14} aria-hidden />
        이 조회는 <b>기록에 남습니다</b>. 화면에는 <b>시각 · 상태만</b> 표시되며, 휴가 종류나 사유는
        결재 문서 안에서만 볼 수 있습니다.
      </p>

      {error && (
        <div role="alert" className="anom__err">
          <EmptyState
            icon={forbidden ? Shield : AlertCircle}
            title={forbidden ? '볼 수 있는 권한이 없습니다' : '이상 근태를 불러오지 못했습니다'}
            description={error}
            action={forbidden ? undefined : <Button variant="secondary" onClick={() => void load()}>다시 시도</Button>}
          />
        </div>
      )}

      {!error && (
        <>
          {data && (
            <Tabs
              variant="pill"
              ariaLabel="걸러 보기"
              items={tabs}
              value={filter}
              onChange={(k) => setFilter(k as AnomalyFilter)}
            />
          )}

          <section className="anom__list" aria-label="이상 근태 목록">
            <Table
              caption="이상 근태 목록"
              columns={columns}
              rows={rows}
              rowKey={(r) => `${r.kind}-${r.userId}-${r.workDate}`}
              loading={loading && !data}
              rowClassName={(r) => `anom__row is-${ANOMALY_TONE[r.kind]}`}
              empty={data && data.counts.total === 0
                ? (
                  <EmptyState
                    icon={CheckCircle}
                    title="어긋난 것이 없습니다."
                    description="오늘 기준으로 미출근도, 퇴근 누락도 없습니다."
                  />
                )
                : (
                  <EmptyState
                    title="고른 조건에 해당하는 사람이 없습니다."
                    description="다른 종류를 고르거나 「전부」로 돌아가 보세요."
                    action={<Button variant="secondary" size="sm" onClick={() => setFilter('ALL')}>전부 보기</Button>}
                  />
                )}
            />
          </section>
        </>
      )}
    </div>
  );
}
